import {GameApiBase, Inject, ModelEvents, GameRandom, Singleton} from "@denishussar/html5-game-engine";
import {GameModel, GameModalsEnum} from "../Models/GameModel";
import { GameStorageManager} from "@denishussar/html5-game-engine/dist";
import { KykkaState } from "../Constants/KykaState";
import { GameEventsConstants } from "../Constants/GameEventsConstants";
import { FieldTypeConstants } from "../Constants/FieldTypeConstants";
import { CameraConstants } from "../Constants/CameraConstants";
import { PlayerTurnConstant } from "../Constants/PlayerTurnConstant";
import { HIT_FOR_ROUND, HIT_FOR_TURN, ROUNDS_TOTAL } from "../Constants/RulesCommonConstants";
import { PlayerTypeConstants } from "../Constants/PlayerTypeConstants";
import { KykkaModel } from "../Models/KykkaModel";
import { DifficultyConstants } from "../Constants/DifficultyConstants";
import { ConverUnitHelpers } from "../Helpers/Helpers";
import { LocalizationContents } from "../LocalizationContents";

interface IKykkaState {
    x: number,
    y: number,
    z: number,
    rotationX: number,
    rotationY: number,
    rotationZ: number,
}
;
@Singleton
export class GameLogic {
    constructor(@Inject private _model: GameModel,
                @Inject private _random: GameRandom,
                @Inject private _modelEvents: ModelEvents,
                @Inject private _arenaApi: GameApiBase,
                @Inject private _storageManager: GameStorageManager) {
        this.load();
        LocalizationContents.setLanguage(this._model.lang);
        this.setPlayerAType(PlayerTypeConstants.Kid);
    }

    public get isPlayerTurn() {
        return this._model.currentTurn === PlayerTurnConstant.A;
    }

    public get currentPlayer() {
        return this._model.currentTurn === PlayerTurnConstant.A
            ? this._model.playerA : this._model.playerB;
    }

    public load() {
        this._storageManager.loadModel("-game", this._model, true, true, (data: any) => {
            return data;
        });
    }

    public save() {
        this._storageManager.saveModel("-game", this._model, {
            lastScore: true,
            tutorialWasShow: true,
            isMusicOn: true,
            isSoundsOn: true,
            lang: true,
            records: Object.keys(this._model.records).reduce((acc, k) => ({...acc, [k]: true}) , {}),
            player: {
                guid: true,
                name: true
            }
        }, true, true, (data) => {
            return data;
        });
    }

    public startNewGame() {
        this.clearGame();
        this.resetGame();
        this.hideIntro();
    }

    public startNewRound() {
        this.clearGame();
        this._model.newRound();
        this._modelEvents.dispatch(this._model, GameEventsConstants.updateScores);
    }

    public showGameEnd() {
        this._model.showGame = false;
        this._model.showGameEnd = true;
        this._model.showPopupShading = true;
    }

    public hideGameEnd() {
        this._model.showGameEnd = false;
        this._model.showPopupShading = false;
        this.showLeaderboard();
    }

    public showLeaderboard() {
        this._model.showGame = false;
        this._model.showIntro = false;
        this._model.showLeaderboard = true;
        this._model.showPopupShading = true;
    }

    public hideLeaderboard() {
        this._model.showLeaderboard = false;
        this._model.showPopupShading = false;
    }

    public showMiniGame() {
        this._model.showMiniGame = true;
    }

    public showMainMenuContainer() {
        this._model.showMainMenuContainer = true;
        this._model.isGameReady = false;
        this._model.pause = false;
    }

    public hideMainMenuContainer() {
        this._model.showMainMenuContainer = false;
    }

    public hideMainMenu() {
        this._model.showMainMenu = false;
    }

    public showMainMenu() {
        this._model.showMainMenu = true;
    }

    public hideMiniGame() {
        this._setKykkaStates(this._model.currentTurn);
        this._model.showMiniGame = false;

        if(this._model.tutorialWasShow) {
            this._model.isGameReady = true;
        }
    }

    public showIntro() {
        this.hideLeaderboard();
        this._model.showIntro = true;
        this._model.showPopupShading = true;
        this._model.showGame = false;
    }

    public hideIntro() {
        this._model.showIntro = false;
        this._model.showPopupShading = false;
        this._model.showTutorial = true;
        this._model.showPlayController = true;
        this._model.showGame = true;
    }

    public showPlayController() {
        this._model.showIntro = true;
        this._model.showPopupShading = true;
        this._model.showMainMenuContainer = true;
        this._model.showTutorial = false;
        this._model.showPlayController = false;
        this._model.showGame = false;
    }

    public resetGame() {

        this._model.newGame();
    }

    public clearGame() {
        this._model.isFreeField = true;

    }

    private _movePointAtAngle(pos: [number, number], angle, distance): [number, number] {
        return [
            pos[0] + (Math.sin(angle) * distance),
            pos[1] - (Math.cos(angle) * distance)
        ];
    }

    private _getAngle(pos: [number, number], to: [number, number]): number {
        return Math.atan2(pos[1] - to[1], pos[0] - to[0]);
    }

    public setPlayerAngle(directionX, directionY) {
        this.currentPlayer.angleX = directionX;
        this.currentPlayer.angleY = directionY;
    }

    public setPlayerPower(power: number) {
        this.currentPlayer.power = power;
    }

    public setPlayerAnglePower(anglePower: number) {
        this.currentPlayer.anglePower = anglePower;
    }

    public setPlayerX(x: number) {
        this.currentPlayer.x = x;
        this.currentPlayer.x3d = ConverUnitHelpers.unit2DTomm(x);
    }

    /**
     * hitStart
     * Call after when start
     */
    public hitStart() {
        this.currentPlayer.hits++;
        this._modelEvents.dispatch(this._model, GameEventsConstants.hitStart);
    }

    /**
     * hitStart
     * Call after when start
     */
    public preventStart() {
        this._modelEvents.dispatch(this._model, GameEventsConstants.preventStart);
        this._modelEvents.dispatch(this.currentPlayer, GameEventsConstants.preventStart);
    }

    /**
     * hitEnd
     * Call after physic engine work end
     */
    public hitEnd() {
        const player = this.currentPlayer;
        const rival = (this.currentPlayer === this._model.playerA) ? this._model.playerB : this._model.playerA;
        const kykkas = this.isPlayerTurn
            ? this._model.kykkaItems
            : this._model.kykkaItemsB;
        const scoreLogItem = {
            city: 0,
            edge: 0,
            line: 0,
            total: 0,
            delta: 0,
            player: this._model.currentTurn
        };

        kykkas.forEach((kykka, index) => {
            switch (kykka.state) {
                case KykkaState.InCity:
                    scoreLogItem.city++;
                    break;
                case KykkaState.OnLine:
                    scoreLogItem.edge++;
                    scoreLogItem.total += 1;
                    break;
                case KykkaState.OutCity:
                    scoreLogItem.total += 3;
                    break;
                case KykkaState.Guest:
                    scoreLogItem.total += 2;
                    break;
                default:
                    console.warn('GameLgogic[hitEnd]: Undefined kykka.state');
                    break;
            }
        });

        if (player.scoresLog.length) {
            scoreLogItem.delta = scoreLogItem.total - + player.scoresLog[0].total;
        } else {
            scoreLogItem.delta = scoreLogItem.total;
        }
        player.scoresLog.unshift(scoreLogItem);
        this._model.scoresLog[0].list.unshift(scoreLogItem);
        const isOut = (k: KykkaModel) => k.state === KykkaState.OutCity;
        const isPlayerHitAllKykkas = this._model.kykkaItems.every(isOut);
        const isBotHitAllKykkas = this._model.kykkaItemsB.every(isOut);

        const isCurrentPlayerHitAll = (this.currentPlayer === this._model.playerA) ? isPlayerHitAllKykkas : isBotHitAllKykkas;
        const isRivalHitAll = (this.currentPlayer === this._model.playerA) ? isBotHitAllKykkas : isPlayerHitAllKykkas;

        // закончен ли арунд?
        if ((player.hits === HIT_FOR_ROUND && rival.hits === HIT_FOR_ROUND)
            || (player.hits === HIT_FOR_ROUND && isRivalHitAll)
            || (rival.hits === HIT_FOR_ROUND && isCurrentPlayerHitAll)) {


            this._model.isGameReady = false;
            this._modelEvents.dispatch(this._model, GameEventsConstants.hitEnd);
            if (this._model.roundNumber === ROUNDS_TOTAL - 1) {
                this._model.playerA.active = false;
                this._model.playerB.active = false;
                this._finishRound();
                this._model.modalState = (this._model.playerA.scores >= this._model.playerB.scores)
                    ? GameModalsEnum.win
                    : GameModalsEnum.lose;
            } else {
                this._finishRound();
                this._model.roundNumber++;
                this.startNewRound();
                this._model.isGameReady = true;
            }
        } else if ((player.hits % HIT_FOR_TURN === 0 && !isRivalHitAll) || isCurrentPlayerHitAll) {
            if(this._model.currentTurn === PlayerTurnConstant.A) {
                this.setPlayerTurn(PlayerTurnConstant.B);
                // this.setCamera(CameraConstants.MediumAwayB);
            } else {
                this.setPlayerTurn(PlayerTurnConstant.A);
                // this.setCamera(CameraConstants.MediumAway);
            }
            this._modelEvents.dispatch(this._model, GameEventsConstants.hitEnd);
        } else {
            this._modelEvents.dispatch(this._model, GameEventsConstants.hitEnd);
        }

        this.setFieldZoom(false);
        this._modelEvents.dispatch(this._model, GameEventsConstants.updateScores);
    }

    /**
     * angleChangeStart
     * Start touch stick of hit direction
     */
    public directionChangeStart() {
        this._modelEvents.dispatch(this._model, GameEventsConstants.directionChangeStart);
        this._modelEvents.dispatch(this.currentPlayer, GameEventsConstants.directionChangeStart);
    }

    /**
     * Set camera model value
     * @param camera CameraModel
     */
    public setCamera(index: CameraConstants) {
        this._model.cameraIndex = index;
        //Object.keys(this._model.camera).forEach((key) => this._model.camera[key] = camera[key]);
    }

    public setFieldZoom(zoom: boolean) {
        switch (this._model.fieldType) {
            case FieldTypeConstants.Big:
                if (zoom) {
                    this.setCamera(this.isPlayerTurn ? CameraConstants.BigClose : CameraConstants.BigCloseB);
                } else {
                    if (this.isPlayerTurn) {
                        this.setCamera(this.currentPlayer.kykkaTouched ? CameraConstants.BigBack : CameraConstants.BigAway);
                    } else {
                        this.setCamera(this.currentPlayer.kykkaTouched ? CameraConstants.BigBackB : CameraConstants.BigAwayB);
                    }
                }
                break;
            case FieldTypeConstants.Medium:
                if (zoom) {
                    this.setCamera(this.isPlayerTurn ? CameraConstants.MediumClose : CameraConstants.MediumCloseB);
                } else {
                    if (this.isPlayerTurn) {
                        this.setCamera(this.currentPlayer.kykkaTouched ? CameraConstants.MediumBack : CameraConstants.MediumAway);
                    } else {
                        this.setCamera(this.currentPlayer.kykkaTouched ? CameraConstants.MediumBackB : CameraConstants.MediumAwayB);
                    }
                }
                break;
            case FieldTypeConstants.Small:
                if (zoom) {
                    this.setCamera(this.isPlayerTurn ? CameraConstants.SmallClose : CameraConstants.SmallCloseB);
                } else {
                    if (this.isPlayerTurn) {
                        this.setCamera(this.currentPlayer.kykkaTouched ? CameraConstants.SmallBack : CameraConstants.SmallAway);
                    } else {
                        this.setCamera(this.currentPlayer.kykkaTouched ? CameraConstants.SmallBackB : CameraConstants.SmallAwayB);
                    }
                }
                break;
        }
        this.currentPlayer.behindCamera = zoom;
        //Object.keys(this._model.camera).forEach((key) => this._model.camera[key] = camera[key]);
    }

    public setCurrentPlayerKykkaTouched(kykkaTouched: boolean) {
        this.currentPlayer.kykkaTouched = kykkaTouched;
    }

    public updateKykkas(kykkas: IKykkaState[]) {
        this._updateKykkasModel(kykkas, this._model.kykkaItems);
    }

    public updateKykkasB(kykkas: IKykkaState[]) {
        this._updateKykkasModel(kykkas, this._model.kykkaItemsB);
    }

    private _updateKykkasModel(kykkas: IKykkaState[], kykkaModels: KykkaModel[]) {

        kykkas.forEach((item, i) => {
            const kykka = kykkaModels[i];
            kykka.x = item.x;
            kykka.y = item.y;
            kykka.z = item.z;
            kykka.rotationX = item.rotationX;
            kykka.rotationY = item.rotationY;
            kykka.rotationZ = item.rotationZ;

            const {leftMost, rightMost, topMost, bottomMost} = kykka;

            if(this.isPlayerTurn) {
                const leftEdgeLineLeft = -0.5 * this._model.fieldSize.width - 0.5 * this._model.fieldSize.lineWidth;
                const leftEdgeLineRight = -0.5 * this._model.fieldSize.width + 0.5 * this._model.fieldSize.lineWidth;
                const rightEdgeLineRight = 0.5 * this._model.fieldSize.width + 0.5 * this._model.fieldSize.lineWidth;
                const rightEdgeLineLeft = 0.5 * this._model.fieldSize.width - 0.5 * this._model.fieldSize.lineWidth;
                const awayLineStart = -this._model.fieldSize.length + 0.5 * this._model.fieldSize.lineWidth;
                const awayLineFinish = -this._model.fieldSize.length - 0.5 * this._model.fieldSize.lineWidth;
                const kykkaLineStart = 0.5 * this._model.fieldSize.lineWidth;

                // console.log(`${leftMost.x > leftEdgeLineRight} && ${rightMost.x < rightEdgeLineLeft}`);
                if (leftMost.x > leftEdgeLineRight && rightMost.x < rightEdgeLineLeft) {
                    if (topMost.y < kykkaLineStart && topMost.y > awayLineStart) {
                        kykka.state = KykkaState.InCity;
                    } else if (topMost.y > kykkaLineStart) {
                        kykka.state = KykkaState.Guest;
                    } else if ((topMost.y < awayLineStart && topMost.y > awayLineFinish)
                        || (bottomMost.y < awayLineStart && bottomMost.y > awayLineFinish)
                        || (bottomMost.y > awayLineFinish && topMost.y < awayLineFinish)
                        || (bottomMost.y > awayLineStart && topMost.y < awayLineStart)
                        ) {
                        kykka.state = KykkaState.OnLine;
                    } else {
                        kykka.state = KykkaState.OutCity;
                    }
                } else if (topMost.y < awayLineFinish
                    && bottomMost.y > kykkaLineStart
                    && this._isOnSideEdge(leftMost, rightMost, leftEdgeLineLeft, leftEdgeLineRight, rightEdgeLineLeft, rightEdgeLineRight)) {
                    kykka.state = KykkaState.OnLine;
                } else {
                    kykka.state = KykkaState.OutCity;
                }
            } else {
                const leftEdgeLineLeft = -0.5 * this._model.fieldSize.width - 0.5 * this._model.fieldSize.lineWidth;
                const leftEdgeLineRight = -0.5 * this._model.fieldSize.width + 0.5 * this._model.fieldSize.lineWidth;
                const rightEdgeLineRight = 0.5 * this._model.fieldSize.width + 0.5 * this._model.fieldSize.lineWidth;
                const rightEdgeLineLeft = 0.5 * this._model.fieldSize.width - 0.5 * this._model.fieldSize.lineWidth;
                const awayLineStart = this._model.fieldSize.distance + this._model.fieldSize.length - 0.5 * this._model.fieldSize.lineWidth;
                const awayLineFinish = this._model.fieldSize.distance + this._model.fieldSize.length + 0.5 * this._model.fieldSize.lineWidth;
                const kykkaLineStart = this._model.fieldSize.distance - 0.5 * this._model.fieldSize.lineWidth;

                if (leftMost.x > leftEdgeLineRight && rightMost.x < rightEdgeLineLeft) {
                    // console.log(`222${topMost.y < this._model.fieldSize.length} && ${bottomMost.y < this._model.fieldSize.length}`);
                    if (bottomMost.y > kykkaLineStart && bottomMost.y < awayLineStart) {
                        kykka.state = KykkaState.InCity;
                    } else if (bottomMost.y < kykkaLineStart) {
                        kykka.state = KykkaState.Guest;
                    } else if ((bottomMost.y > awayLineStart && bottomMost.y < awayLineFinish)
                        || (topMost.y > awayLineStart && topMost.y < awayLineFinish)
                        || (bottomMost.y > awayLineFinish && topMost.y < awayLineFinish)
                        || (bottomMost.y > awayLineStart && topMost.y < awayLineStart)
                     ){
                        kykka.state = KykkaState.OnLine;
                    } else {
                        kykka.state = KykkaState.OutCity;
                    }
                } else if (
                    bottomMost.y > awayLineFinish
                    && topMost.y < kykkaLineStart
                    && this._isOnSideEdge(leftMost, rightMost, leftEdgeLineLeft, leftEdgeLineRight, rightEdgeLineLeft, rightEdgeLineRight)){
                    // console.log(`${(leftMost.x > leftEdgeLineLeft && leftMost.x < leftEdgeLineRight)}
                    //     || ${(rightMost.x > leftEdgeLineLeft && rightMost.x < leftEdgeLineRight)}
                    //     || ${(leftMost.x < rightEdgeLineRight && leftMost.x < rightEdgeLineLeft)}
                    //     || ${(rightMost.x < rightEdgeLineRight && rightMost.x > rightEdgeLineLeft)}
                    //     || ${(leftMost.x < leftEdgeLineLeft && rightMost.x > leftEdgeLineLeft)}
                    //     || ${(rightMost.x > rightEdgeLineRight && leftMost.x < rightEdgeLineRight)}`, leftEdgeLineLeft, leftEdgeLineRight );
                    // console.log(`|| ${rightMost.x > leftEdgeLineLeft} && ${rightMost.x < leftEdgeLineRight}`, rightMost.x, leftEdgeLineLeft, leftEdgeLineRight );

                        kykka.state = KykkaState.OnLine;
                } else {
                    kykka.state = KykkaState.OutCity;
                }
            }
        });
    }

    private _isOnSideEdge(leftMost, rightMost, leftEdgeLineLeft, leftEdgeLineRight, rightEdgeLineLeft, rightEdgeLineRight) {
        return (leftMost.x > leftEdgeLineLeft && leftMost.x < leftEdgeLineRight)
            || (rightMost.x > leftEdgeLineLeft && rightMost.x < leftEdgeLineRight)
            || (leftMost.x < rightEdgeLineRight && leftMost.x > rightEdgeLineLeft)
            || (rightMost.x < rightEdgeLineRight && rightMost.x > rightEdgeLineLeft)
            || (leftMost.x < leftEdgeLineLeft && rightMost.x > leftEdgeLineLeft)
            || (rightMost.x > rightEdgeLineRight && leftMost.x < rightEdgeLineRight);
    }

    public setFieldType(type: FieldTypeConstants) {
        this._model.fieldType = type;
    }

    public setPlayerTurn(turn: PlayerTurnConstant) {
        this._setKykkaStates(turn);
        this._model.currentTurn = turn;
        if(this._model.tutorialWasShow) {
            this._activePlayerByTurn();
        }
    }

    public setPlayerAType(type: PlayerTypeConstants) {
        this._model.playerA.type = type;
        const list = [
            PlayerTypeConstants.Man,
            PlayerTypeConstants.Oldman,
            PlayerTypeConstants.Kid,
            PlayerTypeConstants.Woman
        ];
        const index = list.indexOf(type);
        list.splice(index,1);

        this._model.playerB.type = list[this._random.integer(0, list.length)];
    }

    public setDifficulty(level: DifficultyConstants) {
        this._model.playerB.difficulty = level;
    }

    public setRightHand(isRightHand: boolean) {
        this._model.playerA.isRightHand = isRightHand;
    }

    public pause() {
        if (!this._model.showGame) {
            return;
        }

        this._model.pause = !this._model.pause;

        this._model.showPopupShading = this._model.pause;

        if (this._model.pause) {
            this._model.modalState = GameModalsEnum.pause;
        }
    }


    public showPauseMenu() {
        this._model.modalState = GameModalsEnum.pause;
    }

    public settings() {
        this._model.modalState = GameModalsEnum.settings;
    }

    public charSelect() {
        this._model.modalState = GameModalsEnum.charSelect;
    }

    public records() {
        this._model.modalState = GameModalsEnum.record;
    }

    public play() {
        this._model.showPlayController = true;
    }

    public exitMenu() {
        this._model.modalState = GameModalsEnum.exitMenu;
    }

    public help() {
        console.warn('help');

        this._model.modalState = GameModalsEnum.help;
    }

    public showSpecialWindow() {
        this._model.showSpecialWindow = true;
    }

    public update(frameRate: number) {
        if (this._model.pause) {
            return;
        }

    }

    public hidenModal() {
        if (this._model.pause && this._model.modalState !== GameModalsEnum.pause) {
            this._model.modalState = GameModalsEnum.pause;
        } else {
            // первая игра (видим help впервые)
            if (this._model.modalState === GameModalsEnum.help && !this._model.tutorialWasShow) {
                this._model.tutorialWasShow = true;
                this.save();
                this._activePlayerByTurn();
                this._model.isGameReady = true;
            }

            this._model.modalState = GameModalsEnum.none;
            this._model.showPopupShading = false;

        }
    }

    public setLang(lang) {
        LocalizationContents.setLanguage(lang);
        this._model.lang = lang;
        this.save();
    }

    public setMusicOn(isOn: boolean) {
        this._model.isMusicOn = isOn;
        this.save();
    }

    public setSoundsOn(isOn: boolean) {
        this._model.isSoundsOn = isOn;
        this.save();
    }

    private _setKykkaStates(turn: PlayerTurnConstant) {
        this._model.kykkaItems.forEach(k => k.isActive = (turn === PlayerTurnConstant.A));
        this._model.kykkaItemsB.forEach(k => k.isActive = (turn === PlayerTurnConstant.B));
    }

    private _finishRound() {
        if (this._model.playerA.scores > this._model.records[this._model.fieldType]) {
            this._model.records[this._model.fieldType] = this._model.playerA.scores;
            this.save();
        }
        this._model.scoresLog[0].complete = true;
        this._model.playerA.roundsScoresLog.unshift(this._model.playerA.scores + (HIT_FOR_ROUND - this._model.playerA.hits) * 2);
        this._model.playerB.roundsScoresLog.unshift(this._model.playerB.scores + (HIT_FOR_ROUND - this._model.playerB.hits) * 2);
    }

    private _activePlayerByTurn() {
        this._model.playerA.active = this.isPlayerTurn;
        this._model.playerB.active = !this.isPlayerTurn;
    }
}
